import React, { useState, useEffect } from 'react';
import { Button } from './ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './ui/card';
import { Calendar } from './ui/calendar';
import { X, MapPin, Clock, Calendar as CalendarIcon, User, Mail, Phone } from 'lucide-react';
import { mockData } from '../mock';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const BookingModal = ({ isOpen, onClose, preselectedLocation = null }) => {
  const [selectedDate, setSelectedDate] = useState(null);
  const [selectedTime, setSelectedTime] = useState('');
  const [selectedLocation, setSelectedLocation] = useState(preselectedLocation || 'Ozone Avenue');
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);

  useEffect(() => {
    if (isOpen) setSelectedLocation(preselectedLocation || 'Ozone Avenue');
  }, [isOpen, preselectedLocation]);

  if (!isOpen) return null;

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!selectedDate || !selectedTime || !selectedLocation) {
      alert('Please fill in all required fields');
      return;
    }

    const bookingDetails = {
      ...formData,
      location: selectedLocation,
      date: selectedDate.toLocaleDateString('en-US', { 
        weekday: 'long', 
        year: 'numeric', 
        month: 'long', 
        day: 'numeric' 
      }),
      time: selectedTime
    };

    setIsSubmitting(true);

    try {
      const response = await fetch(`${BACKEND_URL}/api/booking`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(bookingDetails),
      });

      if (response.ok) {
        alert(`✅ Booking Request Submitted Successfully!\n\n📧 Confirmation emails have been sent to:\n• ${bookingDetails.email} (your email)\n• info@spacehash.club (our team)\n\n📋 Booking Details:\nLocation: ${bookingDetails.location}\nDate: ${bookingDetails.date}\nTime: ${bookingDetails.time}\nName: ${bookingDetails.name}\nPhone: ${bookingDetails.phone}\n\n✨ Our team will contact you within 24 hours to confirm your court booking. Thank you!`);
        onClose();
      } else {
        throw new Error('Failed to submit booking');
      }
    } catch (error) {
      console.error('Booking submission error:', error);
      alert('⚠️ There was an error submitting your booking. Please try again or contact us directly at info@spacehash.club or +91 98956 04656.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleInputChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  // Filter out "Coming Soon" locations
  const availableLocations = mockData.locations.filter(loc => !loc.comingSoon);

  // Disable past dates
  const isDateDisabled = (date) => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    return date < today;
  };

  return (
    <div className="booking-modal-overlay" onClick={onClose}>
      <div className="booking-modal-content" onClick={(e) => e.stopPropagation()}>
        <button className="booking-modal-close" onClick={onClose}>
          <X className="w-6 h-6" />
        </button>

        <Card className="booking-card">
          <CardHeader>
            <CardTitle className="booking-modal-title">Book Your Court</CardTitle>
            <CardDescription>Select your preferred date, time, and location</CardDescription>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleSubmit} className="booking-form">
              {/* Location Selection */}
              <div className="booking-section">
                <label className="booking-label">
                  <MapPin className="label-icon" />
                  Select Location *
                </label>
                <select
                  data-testid="booking-location-select"
                  value={selectedLocation}
                  onChange={(e) => setSelectedLocation(e.target.value)}
                  className="booking-select"
                  required
                >
                  <option value="">Choose a location...</option>
                  {availableLocations.map((location, index) => (
                    <option key={index} value={location.name}>
                      {location.name} - {location.address} ({location.courts} courts)
                    </option>
                  ))}
                </select>
              </div>

              {/* Date Selection */}
              <div className="booking-section">
                <label className="booking-label">
                  <CalendarIcon className="label-icon" />
                  Select Date *
                </label>
                <div className="calendar-wrapper">
                  <Calendar
                    mode="single"
                    selected={selectedDate}
                    onSelect={setSelectedDate}
                    disabled={isDateDisabled}
                    className="booking-calendar"
                  />
                </div>
                {selectedDate && (
                  <p className="selected-date-display">
                    Selected: {selectedDate.toLocaleDateString('en-US', { 
                      weekday: 'long', 
                      year: 'numeric', 
                      month: 'long', 
                      day: 'numeric' 
                    })}
                  </p>
                )}
              </div>

              {/* Time Slot Selection */}
              <div className="booking-section">
                <label className="booking-label">
                  <Clock className="label-icon" />
                  Select Time Slot * (1 hour duration)
                </label>
                <div className="time-slots-grid">
                  {mockData.timeSlots.map((slot, index) => (
                    <button
                      key={index}
                      type="button"
                      data-testid={`time-slot-${index}`}
                      className={`time-slot-btn ${selectedTime === slot ? 'selected' : ''}`}
                      onClick={() => setSelectedTime(slot)}
                    >
                      {slot}
                    </button>
                  ))}
                </div>
              </div>

              {/* Personal Information */}
              <div className="booking-section">
                <label className="booking-label">
                  <User className="label-icon" />
                  Your Details
                </label>
                
                <div className="booking-input-group">
                  <input
                    type="text"
                    name="name"
                    data-testid="booking-name-input"
                    value={formData.name}
                    onChange={handleInputChange}
                    placeholder="Full Name *"
                    className="booking-input"
                    required
                  />
                </div>

                <div className="booking-input-group">
                  <Mail className="input-icon" />
                  <input
                    type="email"
                    name="email"
                    data-testid="booking-email-input"
                    value={formData.email}
                    onChange={handleInputChange}
                    placeholder="Email Address *"
                    className="booking-input with-icon"
                    required
                  />
                </div>

                <div className="booking-input-group">
                  <Phone className="input-icon" />
                  <input
                    type="tel"
                    name="phone"
                    data-testid="booking-phone-input"
                    value={formData.phone}
                    onChange={handleInputChange}
                    placeholder="Phone Number *"
                    className="booking-input with-icon"
                    required
                  />
                </div>
              </div>

              {/* Submit Button */}
              <div className="booking-info-note">
                <div className="info-note-header">
                  <span className="info-icon">ℹ️</span>
                  <strong>How it works</strong>
                </div>
                <ul className="info-note-list">
                  <li>Your booking details will be sent to <strong>info@spacehash.club</strong></li>
                  <li>You'll receive a confirmation email at <strong>{formData.email || 'your email'}</strong></li>
                  <li>Our team will confirm your booking within 24 hours</li>
                </ul>
              </div>
              <Button type="submit" data-testid="booking-submit-btn" className="booking-submit-btn" disabled={isSubmitting}>
                {isSubmitting ? 'Submitting...' : 'Book Now'}
              </Button>
            </form>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default BookingModal;
